import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FooterButton } from 'Core/components';
import CustomPropTypes from 'CustomPropTypes';
import { FileUpload } from '../../core/components';
import { useDocumentUpload } from 'Core/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectWaterTowersMaintenanceData } from './store/selectors';
import { showToast } from '../../core/redux/toast/slice';
import { addWaterTowersMaintenanceRecord } from './store/slice';
import ViewAllImagesModal from '../../core/components/viewallimagesmodal/view-all-images-modal';
import WebcamMultiCaptureModal from '../../core/components/modal/webcam-multi-capture-modal';
import SegmentedImagesModal from '../../core/components/modal/segmentedimagesmodal/segmented-images-modal';

const initData = {
  id: 0,
  workOrderNumber: '',
  date: '',
  description: '',
  maintenanceType: '1',
  personnelName: '',
  downTime: '',
  pictures: '',
  question: '',
  answer: '',
  status: '',
  comments: '',
  serviceRecords: [],
  personnelRecords: [
    {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    },
  ],
};

const ignoreKeys = ['pictures', 'comments', 'personnelRecords', 'serviceRecords', 'downTime'];

const WaterTowersMaintenanceRecordAddForm = ({
  data,
  waterTowersId,
  onChanges,
  onCloseModal,
  maintenanceTypeOptions,
}) => {
  const [pictureDocuments, setPictureDocuments] = useState([]);
  const isViewMode = Boolean(data?.id != 0);
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(isViewMode ? data : initData);

  const [uploadedFiles, setUploadedFiles] = useState(
    formData.pictures
      .split(',')
      .filter(file => file.trim() !== '')
      .map((file, index) => ({ id: index + 1, fileName: file, relativePath: file })),
  );

  const [maintenancePersonnels, setMaintenancePersonnels] = useState(formData.personnelRecords);

  const waterTowersMaintenanceData = useSelector(selectWaterTowersMaintenanceData);
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [showImageViewerModal, setShowImageViewerModal] = useState(false);
  const [showManageImagesModal, setShowManageImagesModal] = useState(false);
  const [maintenanceRecordId, setMaintenanceRecordId] = useState(0);
  const [processedImageSegments, setProcessedImageSegments] = useState([]);

  const { uploadDocuments, loading } = useDocumentUpload();

  useEffect(() => {
    if (formData.workOrderNumber === '') {
      setFormData({
        ...formData,
        id: 0,
        workOrderNumber: getNextWorkOrderNumber(waterTowersMaintenanceData),
        waterTowersId: waterTowersId,
        date: new Date().toISOString().split('T')[0],
        personnelName: '',
      });
    }
  }, []);

  const getNextWorkOrderNumber = (maintenanceData) => {
    if (!maintenanceData || !Array.isArray(maintenanceData)) return 'WO-2025-001';
    
    const maxNumber = maintenanceData.reduce((max, record) => {
      const match = record.workOrderNumber?.match(/WO-(\d{4})-(\d+)/);
      if (match) {
        const year = parseInt(match[1]);
        const number = parseInt(match[2]);
        if (year === new Date().getFullYear()) {
          return Math.max(max, number);
        }
      }
      return max;
    }, 0);
    
    const nextNumber = maxNumber + 1;
    return `WO-${new Date().getFullYear()}-${nextNumber.toString().padStart(3, '0')}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePersonnelChange = (index, field, value) => {
    const updatedPersonnels = [...maintenancePersonnels];
    updatedPersonnels[index] = {
      ...updatedPersonnels[index],
      [field]: value,
    };
    setMaintenancePersonnels(updatedPersonnels);
    setFormData(prev => ({
      ...prev,
      personnelRecords: updatedPersonnels,
    }));
  };

  const addPersonnel = () => {
    const newPersonnel = {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    };
    setMaintenancePersonnels([...maintenancePersonnels, newPersonnel]);
  };

  const removePersonnel = (index) => {
    const updatedPersonnels = maintenancePersonnels.filter((_, i) => i !== index);
    setMaintenancePersonnels(updatedPersonnels);
    setFormData(prev => ({
      ...prev,
      personnelRecords: updatedPersonnels,
    }));
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadCategory', 'WATER_TOWERS_MAINTENANCE');
      
      const result = await uploadDocuments(formData);
      if (result) {
        const newFile = {
          id: Date.now(),
          fileName: file.name,
          relativePath: result.relativePath,
        };
        setUploadedFiles(prev => [...prev, newFile]);
        return result.relativePath;
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      dispatch(showToast({
        message: 'Error uploading file',
        type: 'danger',
      }));
    }
    return null;
  };

  const handleSave = async () => {
    try {
      // Validate waterTowersId before saving
      if (!waterTowersId || waterTowersId === 0 || waterTowersId === '0') {
        console.error('Invalid Water Tower ID:', waterTowersId);
        dispatch(showToast({
          message: 'Invalid Water Tower ID. Please close and reopen the form.',
          type: 'danger',
        }));
        return;
      }

      console.log('Saving maintenance record with waterTowersId:', waterTowersId);

      const payload = {
        ...formData,
        waterTowerId: waterTowersId,
        pictures: uploadedFiles.map(file => file.relativePath).join(','),
        PersonnelRecords: maintenancePersonnels || [],
        ServiceRecords: formData.serviceRecords || [],
        SafetyChecklist: formData.safetyChecklist || [],
      };

      console.log('Water Towers Maintenance Record Payload:', payload);

      // Remove ignored keys from payload
      ignoreKeys.forEach(key => delete payload[key]);

      const result = await dispatch(addWaterTowersMaintenanceRecord(payload));
      
      if (result.payload) {
        dispatch(showToast({
          message: 'Water Towers maintenance record saved successfully',
          type: 'success',
        }));
        onCloseModal();
      }
    } catch (error) {
      console.error('Error saving water towers maintenance record:', error);
      dispatch(showToast({
        message: 'Error saving water towers maintenance record',
        type: 'danger',
      }));
    }
  };

  const handleSerialPlateUpload = (imageUrl) => {
    console.log('Serial plate image uploaded:', imageUrl);
    setFormData(prev => ({
      ...prev,
      pictures: prev.pictures ? `${prev.pictures},${imageUrl}` : imageUrl,
    }));
  };

  return (
    <div className="maintenance-record-form">
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">Work Order Number*</label>
          <input
            type="text"
            className="form-control"
            value={formData.workOrderNumber}
            onChange={e => handleInputChange('workOrderNumber', e.target.value)}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Date*</label>
          <input
            type="date"
            className="form-control"
            value={formData.date}
            onChange={e => handleInputChange('date', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <label className="form-label">Description*</label>
          <textarea
            className="form-control"
            rows="4"
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            placeholder="Enter maintenance description"
            required
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">Maintenance Type*</label>
          <select
            className="form-control"
            value={formData.maintenanceType}
            onChange={e => handleInputChange('maintenanceType', e.target.value)}
            required
          >
            {maintenanceTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Personnel Name*</label>
          <input
            type="text"
            className="form-control"
            value={formData.personnelName}
            onChange={e => handleInputChange('personnelName', e.target.value)}
            required
          />
        </div>
      </div>

      {/* Pictures Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Pictures</h6>
          <button
            className="btn btn-primary"
            onClick={() => setShowWebcamModal(true)}
          >
            <i className="fas fa-camera me-2"></i>Capture Image
          </button>
        </div>
      </div>

      {/* Service Completed Documents Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Service Completed Documents</h6>
          <div className="d-flex align-items-center mb-2">
            <select className="form-control me-2" style={{ maxWidth: '200px' }}>
              <option>Select document type</option>
              <option>Service Manual</option>
              <option>Warranty</option>
              <option>Certificate</option>
            </select>
            <FileUpload
              onFileSelect={handleFileUpload}
              accept=".pdf,.doc,.docx"
            />
            <button className="btn btn-primary ms-2">Add</button>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>Document Name</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((file, index) => (
                  <tr key={index}>
                    <td>Service Manual</td>
                    <td>{file.fileName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Maintenance Personnel Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Maintenance Personnel</h6>
            <button className="btn btn-info" onClick={addPersonnel}>
              + Add Personnel
            </button>
          </div>
          {maintenancePersonnels.map((personnel, index) => (
            <div key={index} className="row mb-3 border p-3 rounded">
              <div className="col-md-4">
                <label className="form-label">Personnel Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={personnel.personnelName}
                  onChange={e => handlePersonnelChange(index, 'personnelName', e.target.value)}
                  placeholder="Personnel Name"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Start time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={personnel.startTime}
                  onChange={e => handlePersonnelChange(index, 'startTime', e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">End time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={personnel.endTime}
                  onChange={e => handlePersonnelChange(index, 'endTime', e.target.value)}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-danger"
                  onClick={() => removePersonnel(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Down Time Section */}
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">Down Time</label>
          <input
            type="text"
            className="form-control"
            value={formData.downTime}
            onChange={e => handleInputChange('downTime', e.target.value)}
            placeholder="Auto-calculated based on status"
            readOnly
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="row">
        <div className="col-12 d-flex justify-content-end">
          <button className="btn btn-secondary me-2" onClick={onCloseModal}>
            Cancel
          </button>
          <button className="btn btn-success" onClick={handleSave}>
            Save Water Towers Maintenance Record
          </button>
        </div>
      </div>

      {/* Webcam Modal */}
      {showWebcamModal && (
        <WebcamMultiCaptureModal
          show={showWebcamModal}
          onClose={() => setShowWebcamModal(false)}
          onSubmit={handleSerialPlateUpload}
          context="water-towers"
          buttonLabel="Serial Plate"
          maintenanceData={{
            waterTowers: waterTowersId,
            assetNumber: waterTowersId,
          }}
        />
      )}

      {/* View All Images Modal */}
      {showImageViewerModal && (
        <ViewAllImagesModal
          show={showImageViewerModal}
          onClose={() => setShowImageViewerModal(false)}
          documents={uploadedFiles}
        />
      )}

      {/* Manage Images Modal */}
      {showManageImagesModal && (
        <SegmentedImagesModal
          show={showManageImagesModal}
          onClose={() => setShowManageImagesModal(false)}
          documents={processedImageSegments}
          moldInfoID={0}
          buttonLabel="Serial Plate"
          maintenanceId={maintenanceRecordId}
          context="water-towers"
        />
      )}
    </div>
  );
};

WaterTowersMaintenanceRecordAddForm.propTypes = {
  data: PropTypes.object,
  waterTowersId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChanges: PropTypes.func,
  onCloseModal: PropTypes.func,
  maintenanceTypeOptions: PropTypes.array,
};

export default WaterTowersMaintenanceRecordAddForm;
